import { RfiDoc, RFISettingsDoc } from 'common';
import { RfiUtils } from '..';
import { ConcourseDb, DbListener } from '../../global/ConcourseDb';
import { authFetch, getAccessToken } from '../../global/service/AuthService';

export class RfiService {
  static getRfiSettingsDb(projectId: string): ConcourseDb {
    return ConcourseDb.getInstance(`p-${projectId}-rfis-settings`);
  }

  static async getRfiSettings(projectId: string): Promise<RFISettingsDoc> {
    const db = this.getRfiSettingsDb(projectId);
    const settingsDoc = await db.getDoc('settings');
    return settingsDoc ?? {
      ...RfiUtils.DEFAULT_RFI_SETTINGS,
      fields: [...RfiUtils.DEFAULT_RFI_SETTINGS.fields],
    };
  }

  static async loadRfiSettings(projectId: string, listener: DbListener): Promise<void> {
    const db = this.getRfiSettingsDb(projectId);
    db.addListeners(listener);
    await db.syncRemote();
  }

  static async saveSettings(projectId: string, settings: RFISettingsDoc): Promise<RFISettingsDoc> {
    const db = this.getRfiSettingsDb(projectId);
    settings._id = 'settings';
    return await db.saveDoc(settings);
  }

  static getRfisDb(projectId: string): ConcourseDb {
    return ConcourseDb.getInstance(`p-${projectId}-rfis`);
  }

  /**
   * To save a draft we put it in the list db with all its data.
   */
  static async saveDraft(projectId: string, detail: RfiDoc): Promise<RfiDoc> {
    const db = this.getRfisDb(projectId);
    const doc = await db.saveDoc({ ...detail });
    return doc as RfiDoc;
  }

  /** Any attachments added while in draft status need to be removed from the list db */
  static async removeAttachment(projectId: string, rfi: RfiDoc, filename: string): Promise<RfiDoc> {
    const db = this.getRfisDb(projectId);
    const result = await db.removeAttachment(rfi._id, filename, rfi._rev);
    if (result && result.ok) {
      return new Promise(resolve => {
        resolve({ ...rfi, _rev: result.rev });
      });
    } else {
      throw new Error('Failed to remove attachment');
    }
  }

  static async saveRfiList(projectId: string, list: RfiDoc[]): Promise<RfiDoc[]> {
    const db = this.getRfisDb(projectId);
    db.bulkDocs(list);
    return await db.getDocs();
  }

  static async saveRfi(projectId: string, rfi: RfiDoc): Promise<RfiDoc> {
    const db = this.getRfisDb(projectId);
    return await db.saveDoc(rfi);
  }

  static async updateRfi(projectId: string, rfi: RfiDoc): Promise<RfiDoc> {
    const db = this.getRfisDb(projectId);
    return await db.update(rfi);
  }

  /**
   * We get the RFI id from the last RFI in the list and increment it by 1.
   * We know this is only based on locally cached ids so the create method will throw errors.
   */
  static async getNextRfiNumber(projectId: string): Promise<string> {
    const db = this.getRfisDb(projectId);
    const docs: RfiDoc[] = await db.getDocs();
    const docsNumbers: number[] = docs.filter(doc => !isNaN(Number(doc.code))).map(doc => Number(doc.code));
    return (docsNumbers.length > 0 ? Math.max(...docsNumbers) + 1 : 1).toString();
  }

  static async createRfi(projectId: string, rfi: RfiDoc): Promise<RfiDoc> {
    const url = globalThis.ccLambdaBaseUrl + '/fn/rfi/create?projectId=' + projectId;
    const response = await authFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({ rfi }),
    });

    if (response.status !== 200) {
      throw new Error(await response.text());
    }

    const json = await response.json();
    return json.rfi;
  }

  /**
   * Complete (or revert) a workflow state server-side.
   * The server handles status_history mutation, auth checks, feed item creation,
   * email dispatch, and CouchDB persistence.
   *
   * Maps to: POST /fn/rfi/rfis/:rfiId/complete-state/projects/:projectId
   */
  static async completeState(projectId: string, rfiDoc: RfiDoc, stateId: string | null, dueByUserIds?: string[], message?: string): Promise<RfiDoc> {
    const url = `${globalThis.ccLambdaBaseUrl}/fn/rfi/rfis/${rfiDoc._id}/complete-state/projects/${projectId}`;
    const response = await authFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({ rfiDoc, stateId, dueByUserIds, message }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Failed to complete RFI state: ${errorData.message || response.statusText}`);
    }

    const json = await response.json();
    return json.rfi as RfiDoc;
  }

  static async notifyStatusComplete(projectId: string, stateId: string, rfi: RfiDoc): Promise<Response> {
    const url = globalThis.ccLambdaBaseUrl + '/fn/rfi/notify-status-complete';
    return await authFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify({ projectId, rfi, stateId }),
    });
  }
}
